/**
 * Pre-paint inline styles of the elements the VCC Style Panel paints, per element.
 *
 * A source element rendered in a `.map()` is on canvas N times sharing one inline
 * `style` object in source, so a draft is painted on every instance — but the
 * instances do not all start from the same inline values. A source that computes one
 * per iteration (`plan.highlighted ? 'yellow' : undefined`) gives each instance its
 * own, and the paint overwrites it. Nothing on the entry can recover that: the draft's
 * `original` is a readout of the *drafted* element, so restoring it onto a sibling
 * pins the drafted instance's value there. Only a per-instance record taken before the
 * first paint can put each instance back.
 *
 * Module-level rather than a component ref because unrelated call sites need the same
 * record: `VCCStyleCascadeEffect` writes it and strips against it, and
 * `revertVccStyleDraftsOnPage` reads it on discard paths that are not React at all and
 * can run while a different node is selected. The Style Panel writes it at both of its
 * own paint sites — the per-keystroke paint, which reaches a newly rendered instance
 * before the effect does, and the cascade re-apply on selection. Whichever writer
 * reaches an element first is the one that records, so no ordering between them has to
 * hold. Keyed weakly, so a rebuilt or unmounted element takes its entry with it.
 */
const instanceStyleBaselines = new WeakMap<Element, Map<string, string>>();

/**
 * Records `instance`'s current inline style as its baseline, once. Subsequent calls are
 * no-ops: re-snapshotting would capture the panel's own paint as the baseline, and the
 * restore paths would then "revert" to a drafted value.
 */
export function snapshotVccInstanceStyleBaseline(instance: HTMLElement): void {
  if (instanceStyleBaselines.has(instance)) return;

  const snapshot = new Map<string, string>();
  for (let i = 0; i < instance.style.length; i++) {
    const prop = instance.style[i];
    if (prop) {
      snapshot.set(prop, instance.style.getPropertyValue(prop));
    }
  }
  instanceStyleBaselines.set(instance, snapshot);
}

/**
 * `instance`'s pre-paint inline values, or `undefined` when none were recorded — the
 * element was never painted, or a rebuild replaced the one that was. A returned map is
 * authoritative: a property missing from it had no inline value before the paint.
 * `undefined` is a restore path's cue to leave the element alone — every paint site
 * snapshots before writing, so nothing painted it and there is nothing to put back.
 */
export function getVccInstanceStyleBaseline(
  instance: Element
): Map<string, string> | undefined {
  return instanceStyleBaselines.get(instance);
}
